// Copyright (c) Ping. All rights reserved.

import React from 'react';
import {
  Pressable,
  StyleSheet,
  Text,
  type TextStyle,
  type ViewStyle,
} from 'react-native';
import { useColorScheme } from '../../foundations/colorScheme';
import { createTheme } from '../../design-system/theme';
import type { NoteFolder } from './types';

export interface NoteFolderRowProps {
  readonly folder: NoteFolder;
  readonly open: boolean;
  readonly onToggle: (folder: NoteFolder) => void;
}

export const NoteFolderRow = React.memo<NoteFolderRowProps>(({ folder, open, onToggle }) => {
  const scheme = useColorScheme();
  const theme = createTheme(scheme === 'dark' ? 'dark' : 'light');

  return (
    <Pressable
      onPress={() => onToggle(folder)}
      accessibilityRole="button"
      accessibilityLabel={folder.title}
      accessibilityState={{ expanded: open }}
      style={({ pressed }) => [
        styles.row,
        {
          backgroundColor: pressed ? theme.colors.surface.tertiary : theme.colors.surface.secondary,
          borderBottomColor: theme.colors.border.secondary,
        },
      ]}
    >
      <Text style={[styles.chevron, { color: theme.colors.text.secondary }]}>
        {open ? '▾' : '▸'}
      </Text>
      <Text style={[styles.title, { color: theme.colors.text.primary }]} numberOfLines={1}>
        {folder.title}
      </Text>
      <Text style={[styles.count, { color: theme.colors.text.tertiary }]}>{folder.noteCount}</Text>
    </Pressable>
  );
});

NoteFolderRow.displayName = 'NoteFolderRow';

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderBottomWidth: StyleSheet.hairlineWidth,
  } satisfies ViewStyle,
  chevron: {
    fontSize: 14,
    width: 16,
    textAlign: 'center',
    marginRight: 8,
  } satisfies TextStyle,
  title: {
    flex: 1,
    fontSize: 14,
    fontWeight: '600',
  } satisfies TextStyle,
  count: {
    fontSize: 12,
    fontWeight: '500',
    marginLeft: 8,
  } satisfies TextStyle,
});
